


















































































import Vue from "vue";
import { RawLocation } from "vue-router";
import Toasted from "vue-toasted";
import moment from "moment";
import BidModel, { ItemState } from "@/models/BidModel";
import AuctionAdminModel, { BidHistoryEntry } from "@/models/AuctionAdminModel";
import session from "@/global/session";
import filters from "@/global/filters";

Vue.use(Toasted, { position: "bottom-left", fullWidth: true, duration: 6000 });

export default Vue.extend({
	name: "AuctionAdmin",

	data() {
		return {
			items: [] as ItemState[],
			auctionStatus: "open" as "open" | "closed",
			amounts: {} as { [key: number]: number },
			simBidder: 999 as number,
			isLoading: true as boolean,
			isBusy: false as boolean,
			historyItem: null as ItemState | null,
			historyBids: [] as BidHistoryEntry[],
			historyLoading: false as boolean,
		};
	},

	created() {
		// Auction manager is hidden when mobile bidding is disabled via Options.
		if (!this.$store.getters.isMobileBiddingEnabled) this.$router.replace({ name: "Admin" });
	},

	async mounted() {
		if (!this.$store.getters.isMobileBiddingEnabled) return;

		window.addEventListener("keydown", this.onKeydown);
		await this.refresh();
		this.isLoading = false;
	},

	beforeDestroy() {
		window.removeEventListener("keydown", this.onKeydown);
	},

	methods: {
		currency(value: number | null): string {
			return filters.currency(value || 0);
		},

		routeSecret(): string | undefined {
			return this.$route.query.secret as string | undefined;
		},

		// The admin session token expires (~12h) while the page stays open, so
		// an admin action can 401 mid-session. Detect that here and force a
		// clean re-login instead of surfacing a misleading "action failed"
		// toast. Returns true when it handled the error (caller should stop).
		handleAuthError(e: any): boolean {
			const status = e && e.response ? e.response.status : null;
			if (status !== 401) return false;

			// When authenticated via a ?secret= URL param there is no session to
			// refresh — a 401 there means the secret itself is wrong.
			if (this.routeSecret()) {
				this.$toasted.error("Admin secret rejected — check the ?secret= value.");
				return true;
			}

			session.clear();
			this.$toasted.error("Your admin session expired — please sign in again.");
			this.$router.push({ name: "BidderLogin", query: { redirect: this.$route.fullPath, context: "admin" } } as RawLocation);
			return true;
		},

		async openHistory(item: ItemState) {
			this.historyItem = item;
			this.historyBids = [];
			this.historyLoading = true;
			try {
				this.historyBids = await AuctionAdminModel.history(item.itemNumber, this.routeSecret());
			} catch (e) {
				if (this.handleAuthError(e)) return;
				this.$toasted.error("Could not load bid history.");
			}
			this.historyLoading = false;
		},

		closeHistory() {
			this.historyItem = null;
		},

		formatTime(iso: string): string {
			return moment(iso).format("MMM D, h:mm:ss A");
		},

		onKeydown(e: KeyboardEvent) {
			if (e.key === "Escape" && this.historyItem) this.closeHistory();
		},

		async refresh() {
			this.isBusy = true;
			try {
				const state = await BidModel.state();
				this.items = state.items;
				this.auctionStatus = state.auctionStatus;
			} catch (e) {
				this.$toasted.error("Could not load auction state.");
			}
			this.isBusy = false;
		},

		async simulate(item: ItemState) {
			if (!this.simBidder) {
				this.$toasted.error("Set a 'simulate as bidder #' value first.");
				return;
			}

			this.isBusy = true;
			try {
				const amount = this.amounts[item.itemNumber] || null;
				const result = await AuctionAdminModel.simulateBid(item.itemNumber, this.simBidder, amount, this.routeSecret());

				if (result.accepted) {
					let msg = `Bid ${this.currency(result.amount)} placed as #${result.bidNumber} on item ${result.itemNumber}.`;
					if (result.outbidLeaderBidNumber) {
						msg += result.outbidTextSent ? ` Outbid text sent to #${result.outbidLeaderBidNumber}.` : ` (No phone on file for outbid #${result.outbidLeaderBidNumber}.)`;
					}
					this.$toasted.success(msg);
					this.$set(this.amounts, item.itemNumber, undefined as any);
				} else {
					this.$toasted.error(`Rejected: ${result.reason}. Minimum is ${this.currency(result.minimumNextBid || 0)}.`);
				}
			} catch (e) {
				if (this.handleAuthError(e)) return;
				this.$toasted.error("Simulate bid failed — check the admin secret.");
			}
			await this.refresh();
			this.isBusy = false;
		},

		async resetItem(item: ItemState) {
			if (!window.confirm(`Clear all bids on item ${item.itemNumber}?`)) return;
			this.isBusy = true;
			try {
				await AuctionAdminModel.resetItem(item.itemNumber, this.routeSecret());
				this.$toasted.success(`Cleared bids on item ${item.itemNumber}.`);
			} catch (e) {
				if (this.handleAuthError(e)) return;
				this.$toasted.error("Reset failed.");
			}
			await this.refresh();
			this.isBusy = false;
		},

		async resetAll() {
			if (!window.confirm("Clear ALL bids for every item? This cannot be undone.")) return;
			this.isBusy = true;
			try {
				await AuctionAdminModel.resetAll(this.routeSecret());
				this.$toasted.success("All bids cleared.");
			} catch (e) {
				if (this.handleAuthError(e)) return;
				this.$toasted.error("Reset failed.");
			}
			await this.refresh();
			this.isBusy = false;
		},
	},
});
